import { debounce } from 'lodash'
import uniqBy from 'lodash/uniqBy'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { GroupWithNesting, IndentedNestedGroup } from 'types/group'

import {
  flattenGroupsWithIndent,
  getFilteredFoldersAndSubfolders,
  isSearchedGroupSelected,
  selectedIdsKeys,
  updateGroupsExpanded,
} from '../utils'

export const useFilteredUnselectedGroups = (
  groups: IndentedNestedGroup[],
  selectedGroups: IndentedNestedGroup[],
  searchTerm: string,
): IndentedNestedGroup[] => {
  const selectedIds = useMemo(
    () => new Set(selectedGroups.map(selectedIdsKeys)),
    [selectedGroups],
  )

  const filterGroup = (
    group: IndentedNestedGroup,
  ): IndentedNestedGroup | null => {
    const { isMatchingSearchTerm, isSelected } = isSearchedGroupSelected(
      selectedIds,
      searchTerm,
      group,
    )

    const folders = getFilteredFoldersAndSubfolders(
      group?.folders,
      selectedIds,
      searchTerm,
    )

    let unselectedGroup: IndentedNestedGroup | null = {
      ...group,
      folders,
    }

    if (!searchTerm.length && isSelected) unselectedGroup = null

    if (
      (isSelected && !folders?.length) ||
      (!isMatchingSearchTerm && !folders?.length) ||
      (isSelected && isMatchingSearchTerm)
    )
      unselectedGroup = null

    return unselectedGroup
  }

  const unselectedGroups = useMemo(() => {
    const unselected = groups
      .filter(g => !g.parent)
      .map(filterGroup)
      .filter(Boolean) as IndentedNestedGroup[]

    return uniqBy(unselected, 'id')
  }, [groups])

  return unselectedGroups
}

export const useDebouncedGroupSearch = (groups: GroupWithNesting[]) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentGroups, setCurrentGroups] = useState<IndentedNestedGroup[]>(
    () => groups.slice(0, 5),
  )
  const [isSearching, setIsSearching] = useState(false)

  const initialGroups = searchTerm?.length ? groups : groups.slice(0, 5)

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string, searchedGroups: GroupWithNesting[]) => {
        if (term?.length) setIsSearching(!!term.length)
        const filteredIndentedGroups = flattenGroupsWithIndent(
          searchedGroups,
          term,
        )
        setCurrentGroups(filteredIndentedGroups)
        setIsSearching(false)
      }, 100),
    [],
  )

  const handleToggle = (id: number) => {
    setCurrentGroups(prev => {
      return updateGroupsExpanded(id, prev)
    })
  }

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchTerm(value)
      debouncedSearch(value, groups)
    },
    [debouncedSearch, groups],
  )

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [debouncedSearch])

  useEffect(() => {
    if (!searchTerm?.length) {
      setCurrentGroups(initialGroups)
    }
  }, [searchTerm])

  return {
    searchTerm,
    currentGroups,
    handleToggle,
    handleSearchChange,
    isSearching,
  }
}
